import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();

  useSEO({ robots: decideRobots({ pageType: "auth" }), ...buildPageMeta("auth") });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      nav("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="register-page">
      <p className="eyebrow">{t("register.eyebrow")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{t("register.title")}</h1>
      <p className="mt-2 text-sm text-[#5F6B61]">{t("register.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4 card-clay p-7" data-testid="register-form">
        <Input label={t("register.name")} value={name} onChange={setName} testId="reg-name"/>
        <Input label={t("register.email")} type="email" value={email} onChange={setEmail} testId="reg-email"/>
        <Input label={t("register.password")} type="password" value={password} onChange={setPassword} testId="reg-password"/>
        {error && <div className="text-sm text-[#3F6147] bg-[#E9F0E0] border border-[#5B8C66]/20 rounded-xl px-4 py-3" data-testid="register-error">{error}</div>}
        <button type="submit" disabled={loading} className="btn-pill w-full px-5 py-3 bg-[#5B8C66] text-white font-bold hover:bg-[#3F6147] transition disabled:opacity-60" data-testid="register-submit">
          {loading ? t("register.submitting") : t("register.submit")}
        </button>
      </form>

      <p className="mt-6 text-sm text-center text-[#5F6B61]">
        {t("register.have_account")} <Link to="/login" className="text-[#5B8C66] font-bold">{t("register.signin")}</Link>
      </p>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
        data-testid={testId}
      />
    </div>
  );
}
